/**
 * Shared chime helper for the Rest / Hold timers.
 *
 * Problem we solve here:
 *   - When Spotify Web Playback SDK is streaming audio, the browser can put
 *     other AudioContexts into a `suspended` state. Scheduling oscillators on
 *     a suspended context silently fails — the user hears nothing.
 *   - Our previous gain (0.25) was also too low to be audible next to music
 *     even when the context was running.
 *
 * Fix:
 *   - Reuse ONE global AudioContext across timers (browsers limit how many
 *     can be created concurrently and SDK already has its own).
 *   - Call `ctx.resume()` before every chime — handles the suspended case.
 *   - Boost peak gain to 0.6 with a fast attack/release envelope so the chime
 *     punches through over music without being grating.
 *   - Fall back to a hidden HTMLAudioElement with a tiny synthesized WAV
 *     data URL when AudioContext is unavailable (rare).
 */

let _ctx = null;
let _fallbackAudio = null;

const getCtx = () => {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!_ctx) _ctx = new Ctx();
  return _ctx;
};

const ensureRunning = async (ctx) => {
  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch (err) {
      // Without a recent user gesture Safari/iOS rejects resume() — chime
      // will be silent until the user taps something. Log for visibility.
      console.debug("AudioContext.resume() rejected", err);
    }
  }
};

const scheduleBeep = (ctx, freq, start, dur, peak = 0.6) => {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.frequency.value = freq;
  o.type = "sine";
  o.connect(g);
  g.connect(ctx.destination);
  const t0 = ctx.currentTime + start;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.015);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
};

/**
 * A tiny single-cycle WAV (440Hz sine, 80ms, 8-bit, mono) used as a last-resort
 * fallback when AudioContext is unavailable. Browsers ALWAYS mix Audio elements
 * with other media regardless of audio session state.
 */
const FALLBACK_WAV = "data:audio/wav;base64,UklGRoQAAABXQVZFZm10IBAAAAABAAEAESsAABErAAABAAgAZGF0YWAAAACAioWQjJSPlYqMioqJh4N+eXdyc3FxcXFydHd6foKHi5KWmZ2eoJ+enJqYlpKQjouHhYJ+e3p4dnZ2eHl7fX+ChomMjpCSkpKSk5OTk5OTkpKSkpGRkZGRkZGRkZGRkQ==";
const getFallback = () => {
  if (typeof window === "undefined") return null;
  if (!_fallbackAudio) {
    _fallbackAudio = new Audio(FALLBACK_WAV);
    _fallbackAudio.volume = 0.7;
  }
  return _fallbackAudio;
};

/** Two-note rising chime — used when a rest interval finishes. */
export const playRestEndChime = async () => {
  const ctx = getCtx();
  if (!ctx) {
    getFallback()?.play().catch((err) => console.debug("chime fallback play failed", err));
    return;
  }
  try {
    await ensureRunning(ctx);
    scheduleBeep(ctx, 880, 0, 0.18, 0.55);
    scheduleBeep(ctx, 1320, 0.18, 0.28, 0.6);
  } catch {
    getFallback()?.play().catch((err) => console.debug("chime fallback play failed", err));
  }
};

/** Two-note set-completion chime — fires when a working set or hold ends. */
export const playSetEndChime = async () => {
  const ctx = getCtx();
  if (!ctx) {
    getFallback()?.play().catch((err) => console.debug("chime fallback play failed", err));
    return;
  }
  try {
    await ensureRunning(ctx);
    scheduleBeep(ctx, 988, 0, 0.18, 0.55);
    scheduleBeep(ctx, 1480, 0.2, 0.3, 0.6);
  } catch {
    getFallback()?.play().catch((err) => console.debug("chime fallback play failed", err));
  }
};

/** Three-note "next-up" cue between hold sets. */
export const playNextUpChime = async () => {
  const ctx = getCtx();
  if (!ctx) {
    getFallback()?.play().catch((err) => console.debug("chime fallback play failed", err));
    return;
  }
  try {
    await ensureRunning(ctx);
    scheduleBeep(ctx, 660, 0, 0.12, 0.45);
    scheduleBeep(ctx, 880, 0.14, 0.12, 0.5);
    scheduleBeep(ctx, 1320, 0.28, 0.2, 0.6);
  } catch {
    getFallback()?.play().catch((err) => console.debug("chime fallback play failed", err));
  }
};

/**
 * Call once on a user interaction (e.g. button click) to pre-unlock the
 * AudioContext on Safari iOS — otherwise the first chime can be silent.
 */
export const primeChime = async () => {
  const ctx = getCtx();
  if (!ctx) return;
  await ensureRunning(ctx);
};
